import { useCallback, useEffect, useState } from "react";

import { getLoadErrorMessage, isAPIError } from "@/core/services";
import { listCargaTrabalhoObras, listCargaTrabalhoProfissionais } from "./carga-trabalho.gateway";
import type {
  CargaTrabalhoAgrupamento,
  CargaTrabalhoFilters,
  CargaTrabalhoObrasData,
  CargaTrabalhoPeriodo,
  CargaTrabalhoProfissionaisData,
  CargaTrabalhoQueryState,
} from "./carga-trabalho.types";

export const CARGA_TRABALHO_SEARCH_DEBOUNCE_MS = 300;
export const CARGA_TRABALHO_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar a carga de trabalho.";
export const CARGA_TRABALHO_ERROR_MESSAGE = "Não foi possível carregar a carga de trabalho.";

export type CargaTrabalhoData = CargaTrabalhoProfissionaisData | CargaTrabalhoObrasData;

interface UseCargaTrabalhoParams {
  agrupamento: CargaTrabalhoAgrupamento;
  periodo: CargaTrabalhoPeriodo;
  filters: CargaTrabalhoFilters;
}

interface UseCargaTrabalhoResult extends CargaTrabalhoQueryState<CargaTrabalhoData> {
  agrupamento: CargaTrabalhoAgrupamento;
  reload: () => void;
}

const INITIAL: CargaTrabalhoQueryState<CargaTrabalhoData> = {
  status: "loading",
  data: null,
  errorMessage: null,
};

export function useCargaTrabalho({
  agrupamento,
  periodo,
  filters,
}: UseCargaTrabalhoParams): UseCargaTrabalhoResult {
  const [state, setState] = useState(INITIAL);
  const [loadedAgrupamento, setLoadedAgrupamento] = useState(agrupamento);
  const [reloadToken, setReloadToken] = useState(0);

  const { inicio, fim } = periodo;
  const { busca, obraId, embarcacaoId, situacao } = filters;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(() => {
      const request = {
        periodo: { inicio, fim },
        filters: { busca, obraId, embarcacaoId, situacao },
        signal: controller.signal,
      };
      const promise: Promise<CargaTrabalhoData> =
        agrupamento === "profissionais"
          ? listCargaTrabalhoProfissionais(request)
          : listCargaTrabalhoObras(request);

      promise
        .then((data) => {
          if (cancelled) return;
          setLoadedAgrupamento(agrupamento);
          setState({ status: "success", data, errorMessage: null });
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          const isForbidden = isAPIError(error) && error.status === 403;
          setState({
            status: isForbidden ? "forbidden" : "error",
            data: null,
            errorMessage: isForbidden
              ? CARGA_TRABALHO_FORBIDDEN_MESSAGE
              : getLoadErrorMessage(error, CARGA_TRABALHO_ERROR_MESSAGE),
          });
        });
    }, CARGA_TRABALHO_SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [agrupamento, inicio, fim, busca, obraId, embarcacaoId, situacao, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, agrupamento: loadedAgrupamento, reload };
}
